import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form, Modal, Space, Tabs, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  AOS_VALIDATION_MESSAGES,
  DEFAULT_DATE_FORMAT,
  DEFAULT_PAGINATION_TABLE_FIELDS,
  MODULES_KEY,
  PERMISSION_OBJECT,
  POD_DETAIL_TAB_FIELDS,
  POD_ROLES,
} from '../../../common/constants';
import CommonSelect from '../../../components/CommonSelect';
import CommonTable from '../../../components/CommonTable';
import VerifyPermissions from '../../../components/VerifyPermissions';
import { useTableQueryParams } from '../../../hooks/useTableQueryParams';
import MemberDrawer from './components/MemberDrawer';
import {
  ASSIGN_EMPLOYEE,
  ASSIGN_PROJECT_TO_POD,
  REMOVE_PROJECT_FROM_POD,
  UPDATE_ASSIGNMENT,
} from './graphql/Mutations';
import { POD_MEMBERS, POD_PROJECTS, PROJECTS } from './graphql/Queries';

const buildMembersFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
});

const buildProjectsFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
});

function PodDetail() {
  const history = useHistory();
  const { podId } = useParams();
  const [memberForm] = Form.useForm();
  const [projectForm] = Form.useForm();
  const [showMemberDrawer, setShowMemberDrawer] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [editMember, setEditMember] = useState(null);
  const { params: tabParams, setParams: setTabParams } = useTableQueryParams({
    fields: POD_DETAIL_TAB_FIELDS,
  });
  const { params: memberParams, setParams: setMemberParams } =
    useTableQueryParams({
      namespace: 'members',
      fields: DEFAULT_PAGINATION_TABLE_FIELDS,
    });
  const { params: projectParams, setParams: setProjectParams } =
    useTableQueryParams({
      namespace: 'projects',
      fields: DEFAULT_PAGINATION_TABLE_FIELDS,
    });

  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.AOS_PODS,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });

  const [
    fetchMembers,
    { loading: membersLoading, data: membersData },
  ] = useLazyQuery(POD_MEMBERS, { fetchPolicy: 'network-only', onError() {} });

  const [
    fetchProjects,
    { loading: projectsLoading, data: projectsData },
  ] = useLazyQuery(POD_PROJECTS, { fetchPolicy: 'network-only', onError() {} });

  const [assignEmployee, { loading: assigning }] = useMutation(
    ASSIGN_EMPLOYEE,
    {
      onCompleted() {
        setShowMemberDrawer(false);
        setEditMember(null);
        fetchMembers({
          variables: { podId, filters: buildMembersFilter(memberParams) },
        });
      },
    },
  );

  const [updateAssignment, { loading: updatingAssignment }] = useMutation(
    UPDATE_ASSIGNMENT,
    {
      onCompleted() {
        setShowMemberDrawer(false);
        setEditMember(null);
        fetchMembers({
          variables: { podId, filters: buildMembersFilter(memberParams) },
        });
      },
    },
  );

  const [assignProject, { loading: assigningProject }] = useMutation(
    ASSIGN_PROJECT_TO_POD,
    {
      onCompleted() {
        setShowProjectModal(false);
        fetchProjects({
          variables: { podId, filters: buildProjectsFilter(projectParams) },
        });
      },
    },
  );

  const [removeProject] = useMutation(REMOVE_PROJECT_FROM_POD, {
    onCompleted() {
      fetchProjects({
        variables: { podId, filters: buildProjectsFilter(projectParams) },
      });
    },
  });

  useEffect(() => {
    fetchMembers({
      variables: { podId, filters: buildMembersFilter(memberParams) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podId, memberParams]);

  useEffect(() => {
    fetchProjects({
      variables: { podId, filters: buildProjectsFilter(projectParams) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podId, projectParams]);

  const handleMemberTableChange = (pagination) => {
    const { current: currentPage, pageSize } = pagination;
    setMemberParams({
      page: pageSize !== memberParams?.pageSize ? 1 : currentPage,
      pageSize,
    });
  };

  const handleProjectTableChange = (pagination) => {
    const { current: currentPage, pageSize } = pagination;
    setProjectParams({
      page: pageSize !== projectParams?.pageSize ? 1 : currentPage,
      pageSize,
    });
  };

  const handleOpenAddMember = () => {
    setEditMember(null);
    setShowMemberDrawer(true);
  };

  const handleEditMember = (record) => {
    setEditMember(record);
    memberForm.setFieldsValue({
      allocationPct: record.allocationPct,
      isPrimary: record.isPrimary,
      changeReason: '',
    });
    setShowMemberDrawer(true);
  };

  const handleMemberSubmit = (values) => {
    if (editMember) {
      const input = {
        ...values,
        endDate: values.endDate
          ? values.endDate.format('YYYY-MM-DD')
          : undefined,
      };
      updateAssignment({ variables: { where: { id: editMember.id }, input } });
    } else {
      assignEmployee({
        variables: {
          input: {
            podId,
            ...values,
            startDate: values.startDate?.format('YYYY-MM-DD'),
          },
        },
      });
    }
  };

  const handleMemberClose = () => {
    setShowMemberDrawer(false);
    setEditMember(null);
  };

  const handleProjectSubmit = (values) => {
    assignProject({
      variables: { input: { podId, projectId: values.projectId } },
    });
  };

  const handleRemoveProject = (record) => {
    Modal.confirm({
      title: 'Remove Project',
      content: 'Are you sure you want to remove this project from the pod?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () =>
        removeProject({
          variables: { where: { podId, projectId: record.projectId } },
        }),
    });
  };

  const memberColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, r) =>
        r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '-',
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, r) => r.employee?.email || '-',
    },
    {
      title: 'Role',
      dataIndex: 'podRole',
      key: 'podRole',
      render: (v) => (
        <Tag>{POD_ROLES.find((r) => r.value === v)?.label || v}</Tag>
      ),
    },
    {
      title: 'Allocation',
      dataIndex: 'allocationPct',
      key: 'allocationPct',
      render: (v) => `${v}%`,
    },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (v) => (v ? <Tag color="blue">Primary</Tag> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (v) =>
        v ? (
          <Tag color="error">Inactive</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (v) => (v ? dayjs(v).format(DEFAULT_DATE_FORMAT) : '-'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (v) => (v ? dayjs(v).format(DEFAULT_DATE_FORMAT) : '-'),
    },
    ...(isUpdatePermission
      ? [
          {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, record) =>
              !record.endDate ? (
                <Space size="middle">
                  <Tooltip title="Edit Assignment">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleEditMember(record);
                      }}
                    />
                  </Tooltip>
                </Space>
              ) : null,
          },
        ]
      : []),
  ];

  const projectColumns = [
    {
      title: 'Project',
      key: 'project',
      render: (_, r) => r.project?.name || '-',
    },
    {
      title: 'Assigned At',
      dataIndex: 'assignedAt',
      key: 'assignedAt',
      render: (v) => (v ? dayjs(v).format(DEFAULT_DATE_FORMAT) : '-'),
    },
    ...(isUpdatePermission
      ? [
          {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
              <Space size="middle">
                <Tooltip title="Remove Project">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveProject(record)}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const tabBarExtraContent = () => {
    if (tabParams?.tab === 'members' && isCreatePermission) {
      return (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenAddMember}
        >
          Add Member
        </Button>
      );
    }
    if (tabParams?.tab === 'projects' && isCreatePermission) {
      return (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setShowProjectModal(true)}
        >
          Assign Project
        </Button>
      );
    }
    return null;
  };

  const tabItems = [
    {
      key: 'members',
      label: 'Members',
      children: (
        <CommonTable
          size="small"
          bordered
          columns={memberColumns}
          data={membersData?.podMembers?.assignments || []}
          loadingData={membersLoading}
          onChange={handleMemberTableChange}
          rowKey={(obj) => obj?.id}
          onRow={(record) => ({
            onClick: () =>
              isUpdatePermission && !record?.endDate
                ? handleEditMember(record)
                : undefined,
          })}
          rowClassName={(record) =>
            isUpdatePermission && !record?.endDate ? 'pointer' : ''
          }
          paginationConfig={{
            current: memberParams?.page,
            total: membersData?.podMembers?.count,
            pageSize: memberParams?.pageSize,
          }}
        />
      ),
    },
    {
      key: 'projects',
      label: 'Projects',
      children: (
        <CommonTable
          size="small"
          bordered
          columns={projectColumns}
          data={projectsData?.podProjects?.podProjects || []}
          loadingData={projectsLoading}
          onChange={handleProjectTableChange}
          rowKey={(obj) => obj?.id}
          paginationConfig={{
            current: projectParams?.page,
            total: projectsData?.podProjects?.count,
            pageSize: projectParams?.pageSize,
          }}
        />
      ),
    },
  ];

  const podName =
    history.location.state?.podName ||
    membersData?.podMembers?.assignments?.[0]?.pod?.name;

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll pod-detail-card"
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              type="text"
              onClick={() => history.goBack()}
            />
            {podName || 'Pod Detail'}
          </Space>
        }
      >
        <Tabs
          activeKey={tabParams?.tab}
          onChange={(tab) => setTabParams({ tab })}
          tabBarExtraContent={tabBarExtraContent()}
          tabBarStyle={{ paddingLeft: 24, paddingRight: 24 }}
          items={tabItems}
        />

        <MemberDrawer
          form={memberForm}
          open={showMemberDrawer}
          editMember={editMember}
          loading={assigning || updatingAssignment}
          onFinish={handleMemberSubmit}
          onClose={handleMemberClose}
        />

        <Modal
          title="Assign Project"
          open={showProjectModal}
          onCancel={() => setShowProjectModal(false)}
          footer={null}
          destroyOnClose
          afterClose={() => {
            projectForm.resetFields();
            setProjectSearch('');
          }}
        >
          <Form
            form={projectForm}
            layout="vertical"
            onFinish={handleProjectSubmit}
          >
            <Form.Item
              name="projectId"
              label="Project"
              rules={[
                {
                  required: true,
                  message: AOS_VALIDATION_MESSAGES.PROJECT_REQUIRED,
                },
              ]}
            >
              <CommonSelect
                placeholder="Select Project"
                showSearch
                allowClear
                query={PROJECTS}
                responsePath="projects.projects"
                valuePath="id"
                labelPath="name"
                conditionToCheckBeforeQuery={showProjectModal}
                onSearch={setProjectSearch}
                variables={{ filters: { searchString: projectSearch } }}
                useEffectDeps={[projectSearch]}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={assigningProject}
                >
                  Assign
                </Button>
                <Button onClick={() => setShowProjectModal(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
}

export default PodDetail;
